/**
 * TechnicianContext — Provides the "effective" technician identity.
 *
 * For regular technicians, this returns their own auth identity.
 * For admins, it supports impersonation: selecting a team member
 * to view the module as that person.
 *
 * All technician hooks (useMyJobs, useMySchedule, useMyFSRs) read
 * from this context instead of useAuth directly.
 */
import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { functions } from '../config/firebase';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { logCallableFailure } from '../lib/logCallableFailure';

interface TechnicianIdentity {
  uid: string;
  email: string;
  displayName: string;
  isImpersonating: boolean;
}

interface TechnicianContextType {
  /** The effective technician (real user or impersonated) */
  identity: TechnicianIdentity | null;
  /** Whether the current user can impersonate */
  canImpersonate: boolean;
  /** Set impersonation to a specific user */
  impersonate: (email: string, displayName: string, uid: string) => void;
  /** Clear impersonation back to self */
  clearImpersonation: () => void;
}

const TechnicianContext = createContext<TechnicianContextType | undefined>(undefined);

const logImpersonation = httpsCallable(functions, 'admin_logImpersonation');

export function TechnicianProvider({ children }: { children: React.ReactNode }) {
  const { user, isAdmin } = useAuth();
  const { currentOrganization } = useOrganization();
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(null);
  const [impersonatedName, setImpersonatedName] = useState<string | null>(null);
  const [impersonatedUid, setImpersonatedUid] = useState<string | null>(null);

  const canImpersonate = isAdmin();

  const impersonate = useCallback((email: string, displayName: string, uid: string) => {
    if (!canImpersonate || !currentOrganization?.organizationId) return;
    setImpersonatedEmail(email);
    setImpersonatedName(displayName);
    setImpersonatedUid(uid);
    // Fire-and-forget audit log — UI state update is not gated on the write.
    // Errors are logged to the console but do not block the impersonation action.
    logImpersonation({
      organizationId: currentOrganization.organizationId,
      targetUid: uid,
      targetEmail: email,
      targetDisplayName: displayName,
      action: 'start',
    }).catch((err) => {
      logCallableFailure('[TechnicianContext] Failed to log impersonation start:', err);
    });
  }, [canImpersonate, currentOrganization?.organizationId]);

  const clearImpersonation = useCallback(() => {
    // Log the stop event before clearing state so we still have the target values.
    if (impersonatedUid && impersonatedEmail && impersonatedName && currentOrganization?.organizationId) {
      logImpersonation({
        organizationId: currentOrganization.organizationId,
        targetUid: impersonatedUid,
        targetEmail: impersonatedEmail,
        targetDisplayName: impersonatedName,
        action: 'stop',
      }).catch((err) => {
        logCallableFailure('[TechnicianContext] Failed to log impersonation stop:', err);
      });
    }
    setImpersonatedEmail(null);
    setImpersonatedName(null);
    setImpersonatedUid(null);
  }, [impersonatedUid, impersonatedEmail, impersonatedName, currentOrganization?.organizationId]);

  const identity = useMemo<TechnicianIdentity | null>(() => {
    if (impersonatedEmail && impersonatedName && impersonatedUid) {
      return {
        uid: impersonatedUid,
        email: impersonatedEmail,
        displayName: impersonatedName,
        isImpersonating: true,
      };
    }
    if (user) {
      return {
        uid: user.userId,
        email: user.email,
        displayName: user.displayName,
        isImpersonating: false,
      };
    }
    return null;
  }, [user, impersonatedEmail, impersonatedName, impersonatedUid]);

  const value = useMemo(
    () => ({ identity, canImpersonate, impersonate, clearImpersonation }),
    [identity, canImpersonate, impersonate, clearImpersonation]
  );

  return (
    <TechnicianContext.Provider value={value}>
      {children}
    </TechnicianContext.Provider>
  );
}

/**
 * Access the effective technician identity and impersonation controls.
 */
export function useTechnicianIdentity() {
  const ctx = useContext(TechnicianContext);
  if (ctx === undefined) {
    throw new Error('useTechnicianIdentity must be used within a TechnicianProvider');
  }
  return ctx;
}
